import { ReactElement, CSSProperties, useEffect, useState, useRef, useCallback, forwardRef, PropsWithChildren, useMemo } from 'react';

import ProgressIndicator from 'components/ProgressIndicator';
import classNames from 'classnames';
import XCircle from '@components/Icons/XCircle';
import DotsSixVertical from '@components/Icons/DotsSixVertical';
import { m, useDragControls } from 'framer-motion';
import isInIframe from '@helpers/isInIframe';
import isAndroid from '@helpers/isAndroid';
import isIOS from '@helpers/isIOS';
import Spacer from '@components/Spacer';
import { useIsMobile } from '@/hooks';
import { nanoid } from 'nanoid';

import DynamicDataPanel from '@/components/Questionnaire/form/ConnectedDataPanel';
import s from './Modal.module.scss';
import Portal from '../Portal';
import AiBackdrop from '../AiBackdrop';

type Props = {
  align?: 'left' | 'center' | 'right';
  onClose?: () => void;
  title?: string | ReactElement;
  isFetching?: boolean;
  isAi?: boolean;
  isRightDrawer?: boolean;
  isWide?: boolean;
  hasForm?: boolean;
  isFullScreen?: boolean;
  actions?: Array<ReactElement>;
  hasPadding?: boolean;
  hasUnsavedChanges?: boolean;
  closeIcon?: ReactElement;
  containerStyle?: CSSProperties;
  zIndex?: number;
  hideBottomInset?: boolean;
  shouldCloseOnOutsideClick?: boolean;
  fixedHeight?: string;
  referencedListData?: {
    title: string;
    values: { property: string; value: string }[];
    onEditClick?: () => void;
    type?: 'autofill' | 'ai-autofill' | 'questionnaire';
  };
  drag?: boolean;
};

// eslint-disable-next-line react/display-name
const Modal = forwardRef<HTMLDivElement, PropsWithChildren<Props>>(
  (
    {
      align,
      onClose,
      closeIcon = <XCircle weight="fill" size={24} color="var(--color-gray-100)" />,
      title,
      children,
      isFetching = false,
      isAi,
      isWide,
      isRightDrawer,
      actions = [],
      hasForm: hasFormExternal,
      hasUnsavedChanges = false,
      isFullScreen = false,
      hasPadding = false,
      containerStyle = {},
      zIndex,
      hideBottomInset,
      referencedListData,
      fixedHeight,
      drag,
    },
    ref,
  ) => {
    const isPinnedToBottom = useIsMobile();
    const isDraggable = drag && !isAndroid() && !isIOS() && !isRightDrawer && !isPinnedToBottom && !isFullScreen && !isAi;
    const dragControls = useDragControls();
    const constraintsRef = useRef(null);
    const [hasForm, setHasForm] = useState(hasFormExternal ?? false);
    const [isIconVisible, setIsIconVisible] = useState(false);
    const [isDragged, setIsDragged] = useState(false);

    const modalKey = useMemo(() => nanoid(), []);

    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const modalActions = [...actions];

    const style: CSSProperties = {};
    let containerStyleOverrides: CSSProperties = {};

    if (isFullScreen) {
      style.maxWidth = '100vw';
      style.maxHeight = 'min(min(100vh, 100dvh), var(--host-height))';
      containerStyleOverrides = {
        ...style,
        ...containerStyle,
      };
    }

    if (isPinnedToBottom && !isInIframe()) {
      containerStyleOverrides.position = 'fixed';
      containerStyleOverrides.bottom = '0';
      containerStyleOverrides.width = '100vw';
      containerStyleOverrides.maxWidth = '100vw';
      containerStyleOverrides.borderRadius = '0';
      containerStyleOverrides.maxHeight = '100dvh';
      containerStyleOverrides.height = '100dvh';
    }

    useEffect(() => {
      setIsIconVisible(Boolean(onClose));
    }, [onClose]);

    useEffect(() => {
      if (!isDragged) {
        document.body.style.overflow = 'hidden';
      }
      return () => {
        document.body.style.overflow = 'auto';
      };
    }, [constraintsRef, isDragged]);

    useEffect(() => {
      if (hasFormExternal === undefined) {
        const modalBody = document.getElementById(`modal-body-${modalKey}`);
        if (modalBody) {
          const label = modalBody.querySelector('label');
          setHasForm(!!label);
        }
      } else {
        setHasForm(hasFormExternal);
      }
    }, [hasFormExternal, modalKey]);

    const ESC_CONFIRM_MESSAGE = 'Are you sure you want to close this modal? Changes you made may not be saved.';

    const handleClickIcon: React.MouseEventHandler<HTMLButtonElement> = useCallback(event => {
      event.stopPropagation();
      event.preventDefault();
      if (hasUnsavedChanges || hasForm) {
        if (window.confirm(ESC_CONFIRM_MESSAGE)) {
          if (onCloseRef.current) {
            onCloseRef.current();
          }
        }
      } else if (onCloseRef.current) {
        onCloseRef.current();
      }
    }, []);

    const modalRef = useRef<HTMLDivElement>(null);

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
      if (event.key === 'Escape') {
        if (hasUnsavedChanges || hasForm) {
          if (window.confirm(ESC_CONFIRM_MESSAGE)) {
            if (onCloseRef.current) {
              onCloseRef.current();
            }
          }
        } else if (onCloseRef.current) {
          onCloseRef.current();
        }
        event.stopPropagation();
        return;
      }
      if (event.key === 'Tab') {
        const modal = event.currentTarget;
        const elementsToSelect = 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])';
        const focusableElements = modal.querySelectorAll<HTMLElement>(elementsToSelect);
        if (focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            event.preventDefault();
            lastFocusableElement.focus();
          }
        } else if (document.activeElement === lastFocusableElement) {
          event.preventDefault();
          firstFocusableElement.focus();
        }
      }
    };

    useEffect(() => {
      if (modalRef.current) {
        const elementsToSelect = 'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), label';

        const focusableElements = Array.from(modalRef.current.querySelectorAll<HTMLElement>(elementsToSelect));

        // Find first element that's visible in the modal's viewport
        const firstVisibleElement = focusableElements.find(el => {
          const rect = el.getBoundingClientRect();
          const modalRect = modalRef.current!.getBoundingClientRect();

          return rect.top >= modalRect.top
          && rect.bottom <= modalRect.bottom
          && rect.left >= modalRect.left
          && rect.right <= modalRect.right;
        });

        if (firstVisibleElement) {
          firstVisibleElement.focus();
        } else {
          modalRef.current.focus();
        }
      }
    }, []);

    return (
      <Portal>
        <m.div
          key={modalKey}
          ref={constraintsRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={classNames(
            s.modal__backdrop, {
              [s['modal__backdrop--align-left']]: align === 'left',
              [s['modal__backdrop--align-right']]: align === 'right',
            })}
          style={{
            ...(isInIframe() && { alignItems: 'flex-start', paddingTop: 24 }),
            ...(isDragged && { backgroundColor: 'transparent', pointerEvents: 'none' }),
            zIndex,
          }}
          id={`modal-backdrop-${modalKey}`}
        >
          {isAi && (
            <AiBackdrop />
          )}
          <m.div
            id={`dialog-${modalKey}`}
            ref={modalRef}
            initial={(isAi || isRightDrawer) ? { x: 100 } : { y: 40 }}
            animate={(isAi || isRightDrawer) ? { x: 0 } : { y: 0 }}
            transition={{
              ease: [0.25, 1, 0.5, 1],
            }}
            className={classNames(s.modal__container, {
              [s['modal__container--has-padding']]: hasPadding,
              [s['modal__container--ai']]: isAi,
              [s['modal__container--right']]: isRightDrawer,
              [s['modal__container--wide']]: isWide,
              [s['modal__container--resizable']]: isDraggable,
            })}
            style={{
              ...(isDragged && { border: '1px solid var(--color-gray-25)', boxShadow: 'var(--shadow-lg)', pointerEvents: 'auto' }),
              ...containerStyleOverrides,
            }}
            drag={isDraggable}
            dragListener={false}
            dragControls={dragControls}
            dragConstraints={constraintsRef}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {isFetching && <ProgressIndicator isAbsolute />}
            {title !== null && title !== undefined && (
              <div
                className={s.header}
                {...isFullScreen && { style: { borderRadius: 0 }}}
              >
                {isIconVisible && (
                  <button id="modalCloseButton" className={s.closeButton} onClick={handleClickIcon}>
                    {closeIcon}
                  </button>
                )}
                <span className={classNames("body-sm", {
                  [s['title--ai']]: isAi,
                })}>{title}</span>
                {isDraggable && (
                  <div style={isDraggable
                  && {
                    cursor: 'grab',
                  }
                  || undefined} className={s.drag} onPointerDown={(e) => {
                    if (isDraggable) {
                      dragControls.start(e);
                      setIsDragged(true);
                    }
                  }}>
                    <DotsSixVertical />
                    Drag to move
                  </div>
                )}
              </div>
            )}
            <m.div id={`modal-body-${modalKey}`} layoutScroll className={classNames(s.modal__body, (isAi || isRightDrawer) && s['modal__body-right'])} style={{
              ...style,
              ...(fixedHeight && { height: fixedHeight }),
            }} ref={ref}>
              {title && <Spacer size="24px" />}
              <div
                style={{
                  ...(referencedListData && { alignItems: 'flex-start', display: 'flex' }),
                  ...(fixedHeight && { minHeight: `calc(${fixedHeight} - 24px)` }),
                }}
              >
                {children}
                {referencedListData && <DynamicDataPanel
                  data={referencedListData}
                />}
              </div>
              {((isPinnedToBottom && !hideBottomInset && !isInIframe()) || (isPinnedToBottom && isAndroid()) || (isPinnedToBottom && isIOS())) && (
                <Spacer size="60px" />
              )}
            </m.div>
            {modalActions.length > 0 && <div className={s.modal__actions}>{modalActions}</div>}
          </m.div>
        </m.div>
      </Portal>
    );
  },
);

export default Modal;
